/**
 * RendPleX Rent Scenario Optimizer™ — Premium.
 *
 * Route: /rent-optimizer/:dealId
 *
 * Product intent (see spec):
 *   "If rental income changes, what happens to my cash flow, returns and
 *   portfolio goals?" — NOT "what rent is legally allowed?".
 *
 * All financial math is computed server-side by the SAME engine that
 * produced the property's baseline (calculate_deal_metrics). This page
 * only submits new `annual_income` values and displays deltas.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Target, TrendingUp, TrendingDown, Loader2, Lock, Info, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { RendPlexLogo } from '../components/RendPlexLogo';
import { Footer } from '../components/LegalDisclaimers';
import PortfolioAwareAdvisor from '../components/PortfolioAwareAdvisor';
import { fireChatAnalytics } from '../lib/chatAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ANALYTICS_KEY = '__rent_optimizer__';
const DEBOUNCE_MS = 350;

const fmt$ = (n) => (n === null || n === undefined || Number.isNaN(n) ? '—' : (n < 0 ? `-$${Math.abs(Math.round(n)).toLocaleString()}` : `$${Math.round(n).toLocaleString()}`));
const fmtPct = (n, d = 1) => (n === null || n === undefined || Number.isNaN(n) ? '—' : `${n.toFixed(d)}%`);
const fmtDec = (n, d = 2) => (n === null || n === undefined || Number.isNaN(n) ? '—' : n.toFixed(d));

export default function RentOptimizer() {
  const { dealId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTarget = params.get('target');

  const [ctx, setCtx] = useState(null);          // { deal, baseline, portfolio }
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [errored, setErrored] = useState(false);
  const [scenarios, setScenarios] = useState(null); // [{name, monthlyRent, unitRents, computed}]
  const [running, setRunning] = useState(false);
  const [simResp, setSimResp] = useState(null);   // {baseline, scenarios: [...]}
  const [showTarget, setShowTarget] = useState(false);

  // -------- initial load --------
  useEffect(() => {
    (async () => {
      try {
        const resp = await axios.get(`${BACKEND_URL}/api/rent-optimizer/deal/${dealId}`, { withCredentials: true });
        setCtx(resp.data);
        // Seed 3 scenarios from the baseline monthly rent
        const monthlyBaseline = (resp.data.deal.annual_income || 0) / 12;
        const numUnits = resp.data.deal.num_units || 1;
        const perUnit = numUnits > 1 ? Math.round(monthlyBaseline / numUnits) : monthlyBaseline;
        const buildUnits = () => Array.from({ length: numUnits }, () => Number(perUnit.toFixed(0)));
        setScenarios([
          { name: 'Scenario A', monthlyRent: Math.round(monthlyBaseline * 1.05), unitRents: buildUnits().map((v) => Math.round(v * 1.05)) },
          { name: 'Scenario B', monthlyRent: Math.round(monthlyBaseline * 1.10), unitRents: buildUnits().map((v) => Math.round(v * 1.10)) },
          { name: 'Scenario C', monthlyRent: Math.round(monthlyBaseline * 1.15), unitRents: buildUnits().map((v) => Math.round(v * 1.15)) },
        ]);
        setLocked(false);
        fireChatAnalytics('rent_optimizer_viewed', ANALYTICS_KEY, { deal_id: dealId });
      } catch (e) {
        if (e.response?.status === 403) setLocked(true);
        else if (e.response?.status === 404) {
          toast.error(t('rentOptimizer.dealNotFound'));
          navigate('/portfolio');
        } else if (e.response?.status !== 401) setErrored(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [dealId, navigate, t]);

  // Auto-open Target modal if URL carries ?target=...
  useEffect(() => {
    if (initialTarget !== null && ctx && !showTarget) {
      setShowTarget(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTarget, ctx]);

  // -------- debounced simulate --------
  const timerRef = useRef(null);
  const runSimulate = useCallback((sc) => {
    if (!ctx || !sc) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setRunning(true);
      try {
        const resp = await axios.post(`${BACKEND_URL}/api/rent-optimizer/simulate`, {
          deal_id: dealId,
          scenarios: sc.map((s) => ({ name: s.name, annual_income: s.monthlyRent * 12 })),
        }, { withCredentials: true });
        setSimResp(resp.data);
        // Fire the portfolio_impact_viewed event once per meaningful change
        if (resp.data.scenarios?.some((x) => x.portfolio_impact?.impact !== 0)) {
          fireChatAnalytics('rent_optimizer_portfolio_impact_viewed', ANALYTICS_KEY, { deal_id: dealId });
        }
      } catch { /* ignore transient */ }
      setRunning(false);
    }, DEBOUNCE_MS);
  }, [ctx, dealId]);

  useEffect(() => { runSimulate(scenarios); }, [scenarios, runSimulate]);

  // -------- scenario mutators --------
  const updateScenario = (idx, patch) => {
    setScenarios((prev) => {
      const next = prev.map((s, i) => (i === idx ? { ...s, ...patch } : s));
      fireChatAnalytics('rent_scenario_changed', ANALYTICS_KEY, { idx, key: Object.keys(patch)[0] });
      return next;
    });
  };
  const updateUnitRent = (scIdx, unitIdx, rent) => {
    setScenarios((prev) => prev.map((s, i) => {
      if (i !== scIdx) return s;
      const newUnits = s.unitRents.map((r, u) => (u === unitIdx ? rent : r));
      const total = newUnits.reduce((sum, r) => sum + (Number(r) || 0), 0);
      return { ...s, unitRents: newUnits, monthlyRent: total };
    }));
  };
  const applyTargetAsScenario = (sc, requiredMonthlyRent) => {
    const idx = 0; // Scenario A
    const numUnits = ctx.deal.num_units || 1;
    const perUnit = Math.round(requiredMonthlyRent / numUnits);
    setScenarios((prev) => prev.map((s, i) => (i === idx ? {
      ...s,
      name: t('rentOptimizer.targetScenarioName'),
      monthlyRent: Math.round(requiredMonthlyRent),
      unitRents: Array.from({ length: numUnits }, () => perUnit),
    } : s)));
    fireChatAnalytics('target_rent_used_as_scenario', ANALYTICS_KEY, { rent: requiredMonthlyRent });
    setShowTarget(false);
    toast.success(t('rentOptimizer.targetApplied'));
  };

  // -------- states --------
  if (loading) return <PageShell><LoadingSkeleton t={t} /></PageShell>;
  if (locked) return <PageShell><Locked t={t} navigate={navigate} /></PageShell>;
  if (errored || !ctx) return <PageShell><FailedState t={t} onRetry={() => window.location.reload()} /></PageShell>;

  const { deal, baseline, portfolio } = ctx;
  const numUnits = deal.num_units || 1;
  const currentMonthly = (deal.annual_income || 0) / 12;

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <button
              data-testid="rent-optimizer-back"
              onClick={() => navigate(`/deal/${dealId}`)}
              className="text-sm text-slate-500 hover:text-[#12355B] dark:hover:text-white inline-flex items-center gap-1 mb-3"
            >
              <ArrowLeft className="w-4 h-4" /> {t('rentOptimizer.backToDeal')}
            </button>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center flex-shrink-0">
                <Target className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 data-testid="rent-optimizer-title" className="font-primary font-extrabold text-2xl md:text-3xl text-[#12355B] dark:text-white tracking-tight">
                    {t('rentOptimizer.title')}
                  </h1>
                  <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#12355B] text-white dark:bg-[#45C04A] dark:text-[#12355B]">Premium</span>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{t('rentOptimizer.subtitle')}</p>
              </div>
            </div>
          </div>
          <Button
            data-testid="rent-optimizer-target-btn"
            onClick={() => { fireChatAnalytics('target_rent_calculated', ANALYTICS_KEY, {}); setShowTarget(true); }}
            className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-5 h-11 font-bold"
          >
            <Target className="w-4 h-4 mr-2" /> {t('rentOptimizer.calculateTarget')}
          </Button>
        </div>

        {/* Property + Baseline */}
        <section data-testid="rent-optimizer-baseline" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-6 shadow-card mb-6">
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">{t('rentOptimizer.property')}</p>
          <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4">{deal.property_address}</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <BaselineMetric label={t('rentOptimizer.metrics.totalRent')} value={`${fmt$(currentMonthly)}/mo`} testid="baseline-rent" />
            <BaselineMetric label={t('rentOptimizer.metrics.cashFlow')} value={`${fmt$(baseline.monthly_cash_flow)}/mo`} tone={baseline.monthly_cash_flow >= 0 ? 'pos' : 'neg'} testid="baseline-cf" />
            <BaselineMetric label={t('rentOptimizer.metrics.noi')} value={`${fmt$(baseline.noi)}/yr`} testid="baseline-noi" />
            <BaselineMetric label={t('rentOptimizer.metrics.capRate')} value={fmtPct(baseline.cap_rate)} testid="baseline-cap" />
            <BaselineMetric label={t('rentOptimizer.metrics.dscr')} value={fmtDec(baseline.dscr)} testid="baseline-dscr" />
          </div>
        </section>

        {/* Scenarios */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
          {scenarios.map((sc, idx) => (
            <ScenarioCard
              key={idx}
              idx={idx}
              scenario={sc}
              simResult={simResp?.scenarios?.[idx]}
              baseline={baseline}
              numUnits={numUnits}
              running={running}
              t={t}
              onUpdate={(patch) => updateScenario(idx, patch)}
              onUnitUpdate={(uIdx, rent) => updateUnitRent(idx, uIdx, rent)}
            />
          ))}
        </div>

        {/* Portfolio Impact — only if user has other portfolio deals or a goal */}
        {(portfolio.monthly_cash_flow > 0 || portfolio.cash_flow_goal) && simResp && (
          <PortfolioImpactCard portfolio={portfolio} scenarios={simResp.scenarios} t={t} />
        )}

        {/* Disclaimer */}
        <div data-testid="rent-optimizer-disclaimer" className="rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/30 p-4 flex gap-3">
          <Info className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">{t('rentOptimizer.disclaimer')}</p>
        </div>

        <Footer />
      </div>

      {showTarget && (
        <TargetRentModal
          dealId={dealId}
          ctx={ctx}
          initialTarget={initialTarget !== null ? Number(initialTarget) : 500}
          t={t}
          onClose={() => setShowTarget(false)}
          onApply={applyTargetAsScenario}
        />
      )}
      {/* Portfolio-Aware AI Advisor — grounded on the FIRST scenario's
          simulation (typically the actively-edited "Scenario A"). Slider
          changes silently update this context via useMemo without ever
          calling the LLM — only user-submitted questions trigger a POST. */}
      {simResp?.scenarios?.[0] && (
        <PortfolioAwareAdvisor
          contextType="rent_optimizer"
          context={{
            address: deal.property_address,
            scenario_name: scenarios[0]?.name || 'Scenario A',
            current_monthly_rent: currentMonthly,
            proposed_monthly_rent: scenarios[0]?.monthlyRent,
            baseline_metrics: {
              monthly_cash_flow: baseline.monthly_cash_flow,
              noi: baseline.noi,
              cap_rate: baseline.cap_rate,
              dscr: baseline.dscr,
              roi: baseline.roi,
            },
            scenario_metrics: {
              monthly_cash_flow: simResp.scenarios[0].metrics?.monthly_cash_flow,
              noi: simResp.scenarios[0].metrics?.noi,
              cap_rate: simResp.scenarios[0].metrics?.cap_rate,
              dscr: simResp.scenarios[0].metrics?.dscr,
              roi: simResp.scenarios[0].metrics?.roi,
            },
          }}
          propertyContext={{
            address: deal.property_address,
            purchase_price: deal.purchase_price,
            deal_id: dealId,
          }}
        />
      )}
    </PageShell>
  );
}

/* ================= Sub-components ================= */

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0E1A]">
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <RendPlexLogo />
          <div className="flex items-center gap-3">
            <DarkModeToggle />
            <LanguageSwitcher />
          </div>
        </div>
      </header>
      {children}
    </div>
  );
}

function BaselineMetric({ label, value, tone, testid }) {
  const toneCls = tone === 'pos' ? 'text-[#45C04A]' : tone === 'neg' ? 'text-red-500' : 'text-[#12355B] dark:text-white';
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">{label}</p>
      <p data-testid={testid} className={`text-lg font-bold ${toneCls}`}>{value}</p>
    </div>
  );
}

function ScenarioCard({ idx, scenario, simResult, baseline, numUnits, running, t, onUpdate, onUnitUpdate }) {
  const [showUnits, setShowUnits] = useState(false);
  const diff = scenario.monthlyRent - (baseline?.annual_income || 0) / 12;
  const metrics = simResult?.metrics;
  const deltas = simResult?.deltas;

  return (
    <div data-testid={`scenario-card-${idx}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#131824] p-5 shadow-card">
      <div className="flex items-center justify-between gap-2 mb-3">
        <input
          data-testid={`scenario-name-${idx}`}
          value={scenario.name}
          onChange={(e) => onUpdate({ name: e.target.value })}
          className="font-primary font-bold text-base text-[#12355B] dark:text-white bg-transparent border-none focus:outline-none focus:ring-0 flex-1 min-w-0"
        />
        {running && <Loader2 className="w-4 h-4 animate-spin text-slate-400 flex-shrink-0" />}
      </div>

      <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">{t('rentOptimizer.totalMonthlyRent')}</label>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-slate-400">$</span>
        <Input
          data-testid={`scenario-rent-${idx}`}
          type="number"
          value={scenario.monthlyRent}
          onChange={(e) => {
            const v = Number(e.target.value) || 0;
            const per = numUnits > 1 ? Math.round(v / numUnits) : v;
            onUpdate({ monthlyRent: v, unitRents: Array.from({ length: numUnits }, () => per) });
          }}
          className="h-10 rounded-lg"
        />
      </div>
      <div className={`text-xs font-semibold mb-3 ${diff >= 0 ? 'text-[#45C04A]' : 'text-red-500'}`}>
        {diff >= 0 ? '+' : ''}{fmt$(diff)}/mo {t('rentOptimizer.vsCurrent')}
      </div>

      {numUnits > 1 && (
        <div className="mb-4">
          <button
            data-testid={`toggle-units-${idx}`}
            onClick={() => setShowUnits(!showUnits)}
            className="text-xs font-semibold text-[#1565C0] hover:underline"
          >
            {showUnits ? t('rentOptimizer.hideUnits') : t('rentOptimizer.adjustUnits', { count: numUnits })}
          </button>
          {showUnits && (
            <div className="mt-2 space-y-2">
              {scenario.unitRents.map((r, uIdx) => (
                <div key={uIdx} className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 w-16">{t('rentOptimizer.unit')} {uIdx + 1}</span>
                  <span className="text-slate-400 text-sm">$</span>
                  <Input
                    data-testid={`unit-rent-${idx}-${uIdx}`}
                    type="number"
                    value={r}
                    onChange={(e) => onUnitUpdate(uIdx, Number(e.target.value) || 0)}
                    className="h-8 text-sm rounded-lg"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
        <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-2">{t('rentOptimizer.financialImpact')}</p>
        <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
          <MetricRow label={t('rentOptimizer.metrics.cashFlow')}
            baseline={fmt$(baseline.monthly_cash_flow)}
            scenario={metrics ? fmt$(metrics.monthly_cash_flow) : '—'}
            delta={deltas?.monthly_cash_flow} unit="/mo" testid={`m-cf-${idx}`} />
          <MetricRow label={t('rentOptimizer.metrics.noi')}
            baseline={fmt$(baseline.noi)}
            scenario={metrics ? fmt$(metrics.noi) : '—'}
            delta={deltas?.noi} unit="/yr" testid={`m-noi-${idx}`} />
          <MetricRow label={t('rentOptimizer.metrics.capRate')}
            baseline={fmtPct(baseline.cap_rate)}
            scenario={metrics ? fmtPct(metrics.cap_rate) : '—'}
            delta={deltas?.cap_rate} unit="%" isPercent testid={`m-cap-${idx}`} />
          <MetricRow label={t('rentOptimizer.metrics.dscr')}
            baseline={fmtDec(baseline.dscr)}
            scenario={metrics ? fmtDec(metrics.dscr) : '—'}
            delta={deltas?.dscr} unit="x" isDec testid={`m-dscr-${idx}`} />
          <MetricRow label={t('rentOptimizer.metrics.roi')}
            baseline={fmtPct(baseline.roi)}
            scenario={metrics ? fmtPct(metrics.roi) : '—'}
            delta={deltas?.roi} unit="%" isPercent testid={`m-roi-${idx}`} />
        </div>
      </div>
    </div>
  );
}

function MetricRow({ label, baseline, scenario, delta, unit = '', isPercent, isDec, testid }) {
  const positive = typeof delta === 'number' && delta > 0;
  const negative = typeof delta === 'number' && delta < 0;
  // Icon rendered as a real React element (aria-hidden so no accessibility
  // name leaks through as visible text if the SVG can't render). Wrapping
  // in a fragment guarantees React never coerces the component to string.
  const ArrowIcon = positive ? TrendingUp : negative ? TrendingDown : null;
  const deltaStr = delta === undefined || delta === null || Number.isNaN(delta)
    ? ''
    : isDec ? `${delta > 0 ? '+' : ''}${delta.toFixed(2)}`
    : isPercent ? `${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`
    : `${delta > 0 ? '+' : ''}${fmt$(delta)}`;
  return (
    <div
      data-testid={testid}
      className="grid grid-cols-1 sm:grid-cols-[minmax(5.5rem,auto)_minmax(0,1fr)_auto] gap-y-1 sm:gap-x-3 items-center text-sm py-1.5"
    >
      <span className="font-primary font-semibold text-slate-700 dark:text-slate-200">
        {label}
      </span>
      <span className="font-primary tabular-nums text-slate-500 dark:text-slate-400 inline-flex items-center gap-2 min-w-0 flex-nowrap">
        <span data-testid={`${testid}-baseline`} className="whitespace-nowrap">{baseline}</span>
        <span aria-hidden="true" className="text-slate-400 dark:text-slate-500 select-none shrink-0">→</span>
        <span
          data-testid={`${testid}-scenario`}
          className="font-semibold text-[#12355B] dark:text-white whitespace-nowrap"
        >
          {scenario}
        </span>
      </span>
      {deltaStr ? (
        <span
          data-testid={`${testid}-delta`}
          className={`font-primary tabular-nums font-semibold text-sm whitespace-nowrap inline-flex items-center gap-1 justify-self-start sm:justify-self-end ${
            positive
              ? 'text-[#45C04A]'
              : negative
                ? 'text-red-500'
                : 'text-slate-400'
          }`}
        >
          {ArrowIcon ? (
            <ArrowIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" focusable="false" />
          ) : null}
          <span>{deltaStr}</span>
        </span>
      ) : (
        <span aria-hidden="true" />
      )}
    </div>
  );
}

function PortfolioImpactCard({ portfolio, scenarios, t }) {
  const best = scenarios.reduce((max, s) => (s.portfolio_impact.impact > (max?.portfolio_impact.impact ?? -Infinity) ? s : max), null);
  if (!best) return null;
  const imp = best.portfolio_impact;
  return (
    <section data-testid="portfolio-impact" className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-slate-50 to-white dark:from-slate-800/40 dark:to-[#131824] p-6 mb-6">
      <p className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-2">{t('rentOptimizer.portfolioImpact')}</p>
      <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-4">
        {t('rentOptimizer.bestScenario', { name: best.name || 'Scenario' })}
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400">{t('rentOptimizer.portfolioCashFlow')}</p>
          <p className="text-base font-bold text-[#12355B] dark:text-white">
            {fmt$(imp.portfolio_monthly_cash_flow_current)}<span className="text-slate-400 mx-1">→</span>{fmt$(imp.portfolio_monthly_cash_flow_scenario)}/mo
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase font-bold text-slate-400">{t('rentOptimizer.impact')}</p>
          <p className={`text-base font-bold ${imp.impact >= 0 ? 'text-[#45C04A]' : 'text-red-500'}`}>
            {imp.impact >= 0 ? '+' : ''}{fmt$(imp.impact)}/mo
          </p>
        </div>
        {imp.goal && (
          <div>
            <p className="text-[10px] uppercase font-bold text-slate-400">{t('rentOptimizer.goalProgress')}</p>
            <p className="text-base font-bold text-[#12355B] dark:text-white">
              {imp.goal.current_progress_pct}%<span className="text-slate-400 mx-1">→</span>{imp.goal.scenario_progress_pct}%
            </p>
            {imp.goal.gap_closed_pct > 0 && (
              <p className="text-[11px] text-slate-500 mt-1">
                {t('rentOptimizer.gapClosed', { pct: imp.goal.gap_closed_pct })}
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function TargetRentModal({ dealId, ctx, initialTarget, t, onClose, onApply }) {
  const [target, setTarget] = useState(initialTarget);
  const [solving, setSolving] = useState(false);
  const [result, setResult] = useState(null);

  const solve = async () => {
    setSolving(true);
    setResult(null);
    try {
      const resp = await axios.post(`${BACKEND_URL}/api/rent-optimizer/target-rent`, {
        deal_id: dealId,
        target_monthly_cash_flow: Number(target),
      }, { withCredentials: true });
      setResult(resp.data);
      fireChatAnalytics('target_rent_calculated', ANALYTICS_KEY, { target });
    } catch {
      toast.error(t('rentOptimizer.targetError'));
    }
    setSolving(false);
  };

  // Auto-solve on open when we arrived with a URL target
  useEffect(() => { if (initialTarget !== undefined && initialTarget !== null) solve(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="target-rent-modal" onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-[#131824] rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="font-primary font-extrabold text-lg text-[#12355B] dark:text-white">{t('rentOptimizer.targetTitle')}</h3>
            <p className="text-xs text-slate-500 mt-1">{t('rentOptimizer.targetPrompt')}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="target-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-400 block mb-1">{t('rentOptimizer.targetMonthlyCf')}</label>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-slate-400">$</span>
          <Input data-testid="target-cf-input" type="number" value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-lg" />
          <span className="text-xs text-slate-400">/mo</span>
        </div>

        <Button data-testid="target-solve" onClick={solve} disabled={solving} className="w-full bg-[#12355B] hover:bg-[#1565C0] text-white rounded-xl h-10 mb-4">
          {solving ? <Loader2 className="w-4 h-4 animate-spin" /> : t('rentOptimizer.calculate')}
        </Button>

        {result && (
          <div data-testid="target-result" className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-4 mb-3 space-y-1.5 text-sm">
            <Row label={t('rentOptimizer.currentRentalIncome')} value={`${fmt$(result.current_monthly_rent)}/mo`} />
            <Row label={t('rentOptimizer.requiredRentalIncome')} value={`${fmt$(result.required_monthly_rent)}/mo`} bold />
            <Row label={t('rentOptimizer.difference')} value={`${result.delta_monthly_rent >= 0 ? '+' : ''}${fmt$(result.delta_monthly_rent)}/mo`}
                 tone={result.delta_monthly_rent >= 0 ? 'pos' : 'neg'} />
            <Row label={t('rentOptimizer.projectedCf')} value={`${fmt$(result.resulting_metrics.monthly_cash_flow)}/mo`} />
            {!result.converged && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 pt-2">{t('rentOptimizer.targetNotConverged')}</p>
            )}
          </div>
        )}

        {result && (
          <Button
            data-testid="target-use"
            onClick={() => onApply(null, result.required_monthly_rent)}
            className="w-full bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl h-10 font-bold"
          >
            {t('rentOptimizer.useAsScenario')}
          </Button>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, tone, bold }) {
  const toneCls = tone === 'pos' ? 'text-[#45C04A]' : tone === 'neg' ? 'text-red-500' : 'text-[#12355B] dark:text-white';
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className={`${bold ? 'font-bold' : 'font-semibold'} ${toneCls}`}>{value}</span>
    </div>
  );
}

function LoadingSkeleton({ t }) {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 animate-pulse">
      <div className="h-8 w-72 bg-slate-100 dark:bg-slate-800 rounded mb-6" />
      <div className="h-32 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-4" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
        <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
        <div className="h-64 bg-slate-100 dark:bg-slate-800 rounded-2xl" />
      </div>
    </div>
  );
}

function Locked({ t, navigate }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16">
      <section data-testid="rent-optimizer-locked" className="rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1a4574] p-8 text-white shadow-card">
        <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mb-4">
          <Lock className="w-6 h-6" />
        </div>
        <h1 className="font-primary font-extrabold text-2xl md:text-3xl tracking-tight mb-2">{t('rentOptimizer.title')}</h1>
        <p className="text-white/70 mb-6 max-w-lg">{t('rentOptimizer.lockedSubtitle')}</p>
        <Button
          data-testid="rent-optimizer-upgrade"
          onClick={() => {
            fireChatAnalytics('rent_optimizer_upgrade_clicked', ANALYTICS_KEY, {});
            navigate('/pricing');
          }}
          className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl h-11 px-6 font-bold"
        >
          {t('rentOptimizer.unlockCta')}
        </Button>
      </section>
    </div>
  );
}

function FailedState({ t, onRetry }) {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 text-center">
      <p className="font-semibold text-[#12355B] dark:text-white mb-2">{t('rentOptimizer.failed')}</p>
      <Button onClick={onRetry} variant="outline" className="rounded-xl">{t('rentOptimizer.retry')}</Button>
    </div>
  );
}
